import React, {useEffect, useRef, useState} from "react";
import {FlowData, RegisterFunction} from "../../../../util/types.ts";
import {ScrollToAnchorPoint} from "../../../../util/ScrollToAnchor.tsx";
import {filterIllegalCharcters, scrollToElement} from "../../../../util/helpers.ts";

const devices = [
    {name: "mobil", label: "Mobil"},
    {name: "computer", label: "Bærbar computer eller PC"},
    {name: "tv", label: "TV"},
    {name: "tvbox", label: "TV-boks"},
    {name: "spillekonsol", label: "Spillekonsol"},
    {name: "alarmsystem", label: "Alarmsystem"},
    {name: "alle", label: "Alle mine enheder"},
    {name: "andre", label: "Andre"},
];

interface DeviceErrorComponentProps {
    flowData: FlowData;
    setFlowData: React.Dispatch<React.SetStateAction<FlowData>>;
    register: RegisterFunction;
}

function DeviceInput({
            name,
            label,
            isChecked,
            onToggleChecked,
        }: {
    name: string;
    label: string;
    isChecked: boolean;
    onToggleChecked: (name: string) => void;
}) {
    return (
        <p className={'with-checkbox'}>
            <input
                type={'checkbox'}
                id={`${name}_checkbox`}
                name={name}
                checked={isChecked}
                onChange={() => onToggleChecked(name)}
            />
            <label className={'checkbox-label checkbox-label--montserrat'} htmlFor={`${name}_checkbox`}>
                {label}
            </label>
        </p>
    );
}

export default function DeviceErrorComponent({
        flowData,
        setFlowData,
        register,
    }: DeviceErrorComponentProps) {

    const [deviceError, setDeviceError] = useState<string>('');
    const deviceSectionComponentTop = useRef() as React.MutableRefObject<HTMLDivElement>;

    const handleDeviceToggle = (name: string) => {
        setFlowData((prevFlowData) => {
            const currentDevices = prevFlowData.devices ?? [];
            let newDevices: string[];
            if (currentDevices.includes(name)) {
                newDevices = currentDevices.filter((device) => device !== name);
            } else {
                newDevices = [...currentDevices, name];
            }
            if (devices.length > 0) {
                setDeviceError('');
            }
            return {
                ...prevFlowData,
                devices: newDevices,
            };
        });
    };

    const hasError = deviceError.length > 0;

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let input = event.target.value;

        if (input.length > 64) {
            input = input.substring(0, 64);
        }
        const sanitizedInput = filterIllegalCharcters(input);
        setFlowData((prev) => ({ ...prev, otherDeviceDescription: sanitizedInput }));
        setDeviceError('')
    };

    useEffect((): void => {
        register("DeviceErrorComponent",
            {
                hasErrors: (flowData: FlowData): boolean => {
                    if (!Object.values(flowData.devices ?? {}).some((isSelected) => isSelected)) {
                        setDeviceError("Vælg hvilke enheder du oplever problemer på.");
                        return true
                    }
                    return false
                },
                scrollToError: (): void => {
                    scrollToElement(deviceSectionComponentTop.current)
                },
            },
        )
    }, [register]);

    return (
        <div className={`input-container${hasError ? ' error' : ''}`} ref={deviceSectionComponentTop}>
            <ScrollToAnchorPoint id={'device-section'} className={'id-anchor--selfcare-report-error'}/>
            <p>Hvilke(e) enhed(er) oplever du problemer på?*</p>
            {devices.map((device) => (
                <DeviceInput
                    key={device.name}
                    name={device.name}
                    label={device.label}
                    isChecked={flowData.devices?.includes(device.name) ?? false}
                    onToggleChecked={handleDeviceToggle}
                />
            ))}
            {flowData.devices?.includes("andre") && (
                <p>
                    <input
                        name={'otherDeviceDescription'}
                        type={'text'}
                        value={flowData.otherDeviceDescription}
                        onChange={handleInputChange}
                        placeholder={'Indtast enheder'}
                    />
                </p>
            )}

            {hasError && <p className={'error-message'}>{deviceError}</p>}
        </div>
    );
}
